import React, { useEffect, useState } from 'react';
import { Card, Badge, ErrorNote } from '../ui';

interface Diagnostics {
  appVersion: string;
  dataDir: string;
  database: string;
  controlPort: number;
  counts: { pages: number; content: number; published: number; jobs: number };
  providers: { kind: string; displayName: string; enabled: boolean; executablePath: string | null; lastStatus: string; lastCheckedAt: string | null }[];
}

export function AboutScreen() {
  const [diag, setDiag] = useState<Diagnostics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        setDiag((await window.pagepilot.diagnostics.get()) as Diagnostics);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, []);

  return (
    <div className="max-w-3xl space-y-4">
      <h1 className="text-lg font-semibold">About & Diagnostics</h1>
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-500 text-lg font-bold text-white">▶</div>
        <div>
          <p className="font-semibold">PagePilot AI</p>
          <p className="text-xs text-neutral-500">Autonomous Facebook Content Studio · v{diag?.appVersion ?? '—'}</p>
        </div>
      </div>
      {error && <ErrorNote message={error} />}

      {diag && (
        <>
          <Card title="System">
            <dl className="space-y-1.5 text-sm">
              <Row label="Data folder" value={diag.dataDir} />
              <Row label="Database" value={diag.database} />
              <Row label="Engine control" value={`127.0.0.1:${diag.controlPort}`} />
              <Row label="Content" value={`${diag.counts.pages} pages · ${diag.counts.content} items · ${diag.counts.published} published · ${diag.counts.jobs} jobs`} />
            </dl>
          </Card>

          <Card title="Providers">
            <ul className="space-y-2">
              {diag.providers.map((p) => (
                <li key={p.kind} className="flex items-center gap-2 text-xs">
                  <Badge status={p.lastStatus === 'HEALTHY' ? 'HEALTHY' : p.lastStatus === 'unchecked' ? 'OFFLINE' : 'MISCONFIGURED'} label={p.lastStatus} />
                  <span className="w-40 text-neutral-300">{p.displayName}</span>
                  <span className="truncate text-neutral-500">{p.executablePath ?? (p.enabled ? 'auto-detect' : 'disabled')}</span>
                </li>
              ))}
            </ul>
          </Card>

          <button
            className="rounded-lg bg-neutral-800 px-3 py-1.5 text-sm hover:bg-neutral-700"
            onClick={async () => {
              await navigator.clipboard.writeText(JSON.stringify(diag, null, 2));
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
          >
            {copied ? 'Copied' : 'Copy diagnostics (sanitized)'}
          </button>
        </>
      )}
    </div>
  );
}

function Row(props: { label: string; value: string }) {
  return (
    <div className="flex gap-3 text-sm">
      <dt className="w-32 shrink-0 text-neutral-500">{props.label}</dt>
      <dd className="truncate font-mono text-xs leading-6 text-neutral-300">{props.value}</dd>
    </div>
  );
}
